// Hero section — animated chat mockup + CTA pair.
// Implements the approved Claude Design handoff (Ask Companion Landing.html).
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { Bubble } from "../components/Bubble";
import { CtaPair } from "../components/CtaPair";
import { Mascot, type MascotName } from "../components/Mascot";
import { TreatIcon } from "../components/TreatIcon";

type Step =
  | { type: "user"; text: ReactNode }
  | { type: "ai"; mascot: MascotName; text: ReactNode };

// The approved default — multi-companion thread showing Context + clinical math + writing.
const CONVERSATION: Step[] = [
  { type: "user", text: "What was the SDMA last visit?" },
  {
    type: "ai",
    mascot: "Summi",
    text: (
      <>
        <b>14 µg/dL on Feb 3</b> — up from 12 in November.
      </>
    ),
  },
  {
    type: "user",
    text: "What's the fluid rate for a 22kg dog with increased SDMA?",
  },
  {
    type: "ai",
    mascot: "Sage",
    text: (
      <>
        <b>~69 mL/hr</b> at 1.5× maintenance (22kg × 50 mL/kg/day × 1.5, div.
        24). Reassess hydration after 12h.
      </>
    ),
  },
  {
    type: "user",
    text: "Write me a referral summary for internal medicine at a tertiary care facility.",
  },
  {
    type: "ai",
    mascot: "Quill",
    text: (
      <>
        Done. Referral drafted with history, labs, and current meds — ready to
        review.
      </>
    ),
  },
];

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function ChatMockup() {
  const [visibleCount, setVisibleCount] = useState(() =>
    prefersReducedMotion() ? CONVERSATION.length : 0,
  );

  useEffect(() => {
    if (prefersReducedMotion()) return;
    let cancelled = false;
    const run = async () => {
      // Messages 3, 4, 5 (0-indexed) are longer and need extra read time
      // before the next bubble appears.
      const LONG_MESSAGE_INDICES = new Set([2, 3, 4]);
      for (let i = 0; i < CONVERSATION.length; i++) {
        const afterAiReply = i > 0 && CONVERSATION[i - 1].type === "ai";
        const afterLongMessage = i > 0 && LONG_MESSAGE_INDICES.has(i - 1);
        const delay =
          i === 0
            ? 500
            : (afterAiReply ? 2200 : 1200) + (afterLongMessage ? 600 : 0);
        await new Promise((r) => setTimeout(r, delay));
        if (cancelled) return;
        setVisibleCount(i + 1);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="chat-card relative min-h-[560px] rounded-[24px] bg-[linear-gradient(180deg,#FFFFFF_0%,#F5F3FF_40%,#E0E7FF_100%)] p-[22px] shadow-[0_30px_60px_-30px_rgba(67,56,202,0.35),0_0_0_1px_rgba(99,102,241,0.08)]">
      {/* window chrome — companions trio + patient context */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex">
            {(["Summi", "Sage", "Quill"] as MascotName[]).map((m, i) => (
              <div
                key={m}
                className={`h-8 w-8 overflow-hidden rounded-full shadow-[0_0_0_2px_white,0_2px_4px_-1px_rgba(67,56,202,0.25)] ${
                  i === 0 ? "ml-0" : "-ml-2.5"
                }`}
              >
                <Mascot name={m} size={32} />
              </div>
            ))}
          </div>
          <div>
            <div className="text-[13px] font-semibold leading-[1.2] text-primary">
              Companion
            </div>
            <div className="text-[11px] text-secondary">
              Bella · Golden Retriever · 8y
            </div>
          </div>
        </div>
      </div>

      {/* chat stream */}
      <div className="min-h-[380px] overflow-visible py-1">
        {CONVERSATION.map((s, i) => {
          const visible = i < visibleCount;
          if (s.type === "user") {
            return (
              <Bubble key={i} who="user" visible={visible}>
                {s.text}
              </Bubble>
            );
          }
          return (
            <Bubble key={i} who="ai" mascot={s.mascot} visible={visible}>
              {s.text}
            </Bubble>
          );
        })}
      </div>

      {/* input bar (decorative) */}
      <div className="mt-[18px] flex items-center gap-2.5 rounded-full border border-grindigo-100 bg-white px-3.5 py-2.5 shadow-[0_2px_10px_-4px_rgba(99,102,241,0.12)]">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
            stroke="var(--color-grindigo-500)"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="flex-1 text-[13px] text-placeholder">
          Ask Companion anything…
        </span>
        <div className="grid h-[30px] w-[30px] place-items-center rounded-full bg-indigo-500">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 19V5M5 12l7-7 7 7"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* floating companion trio sticker */}
      <div className="chat-trio absolute -top-[22px] -right-[18px] flex transition-transform duration-[400ms] [transition-timing-function:cubic-bezier(0.2,0.9,0.3,1)] [filter:drop-shadow(0_10px_24px_rgba(67,56,202,0.3))]">
        <div
          style={{ transform: "rotate(-10deg)" }}
          className="chat-trio-mascot -mr-[18px] transition-transform duration-[400ms] [transition-timing-function:cubic-bezier(0.2,0.9,0.3,1)]"
        >
          <Mascot name="Summi" size={64} />
        </div>
        <div
          style={{ transform: "rotate(4deg)" }}
          className="chat-trio-mascot z-[2] -mr-[18px] transition-transform duration-[400ms] [transition-timing-function:cubic-bezier(0.2,0.9,0.3,1)]"
        >
          <Mascot name="Sage" size={72} />
        </div>
        <div
          style={{ transform: "rotate(12deg)" }}
          className="chat-trio-mascot transition-transform duration-[400ms] [transition-timing-function:cubic-bezier(0.2,0.9,0.3,1)]"
        >
          <Mascot name="Quill" size={64} />
        </div>
      </div>
    </div>
  );
}

export function Hero() {
  const ctaRef = useRef<HTMLAnchorElement>(null);

  // Bounce the CTA after 5s of page inactivity; re-arm on any user interaction.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const el = ctaRef.current;
        if (!el) return;
        el.classList.remove("cta-bounce");
        void el.offsetWidth; // reflow so the animation can restart
        el.classList.add("cta-bounce");
        arm();
      }, 5000);
    };
    const events = ["mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) =>
      window.addEventListener(e, arm, {
        passive: true,
      } as AddEventListenerOptions),
    );
    arm();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, arm));
    };
  }, []);

  return (
    <section className="px-[clamp(24px,5vw,80px)] pt-[72px] pb-16">
      <div className="hero-grid mx-auto grid max-w-[1240px] grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] items-center gap-16">
        {/* left column */}
        <div>
          <h1 className="font-companion tracking-[-0.01em] mb-5 text-[clamp(40px,5.5vw,64px)] font-bold leading-[1.05] tracking-[-0.03em] text-primary [text-wrap:balance]">
            The AI Companion for vets who&apos;d rather be with patients.
          </h1>
          <p className="mb-8 max-w-[520px] text-[19px] leading-[1.55] text-secondary [text-wrap:pretty]">
            Ask anything, about any patient, anytime.
          </p>

          <div className="flex flex-col items-center gap-4 sm:items-start">
            <CtaPair
              tone="light"
              size="lg"
              align="start"
              getStartedRef={ctaRef}
              onGetStartedAnimationEnd={(e) =>
                e.currentTarget.classList.remove("cta-bounce")
              }
            />
            <div className="inline-flex items-center gap-2 text-center text-sm text-secondary sm:text-left">
              <span className="inline-flex shrink-0">
                <TreatIcon size={20} />
              </span>
              <span>
                Get <b className="text-primary">500 free Treats</b> when you get
                started with Companion.
              </span>
            </div>
          </div>
        </div>

        {/* right column — chat mockup */}
        <div>
          <ChatMockup />
        </div>
      </div>
    </section>
  );
}
